import React, { useState } from "react";
import {
	Box,
	Divider,
	Link,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
} from "@mui/material";
import "./OrderDetails.css";
import { ASSETS_BASE_URL } from "../../config";
import { useSite } from "../../context/siteContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AddShoppingCart, KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { useCart } from "../../context/cartContext";
import { useAuth } from "../../context/authContext";
import { saveRequestList } from "../../services/productCardService";
import { toast } from "../../context/ToastContext";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import ReplacementPartNotice from "../ReplacementPartNotice/ReplacementPartNotice";

const PAST_SALES_LINE_STATUS_MAP = {
	1: "Open Order",
	2: "Delivered",
	3: "Invoiced",
	4: "Canceled",
};

// Delivery Terms by Legal Entity
const DT_BY_LE = {
	AUSA: [
		{ term: 'PPD-ADD', descriptionKey: 'UI_TEXT.PREPAID_AND_ADD' },
		{ term: 'COD', descriptionKey: 'UI_TEXT.COLLECT_ON_DELIVERY' },
	],
	ACAN: [
		{ term: 'PPD-ADD', descriptionKey: 'UI_TEXT.PREPAID_AND_ADD' },
		{ term: 'COD', descriptionKey: 'UI_TEXT.COLLECT_ON_DELIVERY' },
	],
	EUUK: [
		{ term: 'EXW', descriptionKey: 'SHIPPING.EX_WORKS' },
		{ term: 'DAP', descriptionKey: 'SHIPPING.DELIVERY_AT_PLACE' },
		{ term: 'DDP', descriptionKey: 'SHIPPING.DELIVERY_DUTY_PAID' },
	],
};

// Mode of Delivery by Legal Entity
const DM_BY_LE = {
	AUSA: [
		{ code: 'UPS-GND', description: 'UPS Ground' },
		{ code: 'UPS-1DA', description: 'UPS Next Day Air' },
		{ code: 'UPS-1DM', description: 'UPS Next Day Air AM Delivery' },
		{ code: 'UPS-2DA', description: 'UPS Second Day Air' },
		{ code: 'FedEx-FG', description: 'FedEx Ground' },
		{ code: 'FedEx-F2', description: 'FedEx Second Day Air' },
		{ code: 'Speci-Spec', description: 'Special Instructions' },
	],
	ACAN: [
		{ code: 'UPS-GND', description: 'UPS Ground' },
		{ code: 'UPS-1DA', description: 'UPS Next Day Air' },
		{ code: 'UPS-1DM', description: 'UPS Next Day Air AM Delivery' },
		{ code: 'UPS-2DA', description: 'UPS Second Day Air' },
		{ code: 'FedEx-FG', description: 'FedEx Ground' },
		{ code: 'FedEx-F2', description: 'FedEx Second Day Air' },
		{ code: 'Speci-Spec', description: 'Special Instructions' },
	],
	EUUK: [
		{ code: 'UPS-1DA', description: 'UPS Next Day Air' },
		{ code: 'UPS-1DM', description: 'UPS Next Day Air AM Delivery' },
		{ code: 'UPS-2DA', description: 'UPS Second Day Air' },
		{ code: 'FedEx-FG', description: 'FedEx Ground' },
		{ code: 'FedEx-F2', description: 'FedEx Second Day Air' },
		{ code: 'Speci-Spec', description: 'Special Instructions' },
	],
};


/**
 * Expanded order panel: per-line product table (the name links to the part when it is on the
 * selected site; the replacement caption links to the replacement part when its id is known),
 * tracking, totals and buy-again / request-list actions.
 *
 * @param {Object} props
 * @param {Object} props.orderData Combined order row whose `products[]` lines carry `productsData`.
 * @param {boolean} [props.pastOrder=false] Render the past-sales-order variant (ERP line statuses).
 * @param {boolean} [props.trackingLoading=false] Show tracking skeletons while tracking is fetched.
 * @param {boolean} [props.partDetailsOnly=false] Render only the product table.
 * @returns {JSX.Element}
 */
const OrderDetails = ({ orderData, pastOrder = false, trackingLoading = false, partDetailsOnly = false }) => {
	const safeOrderData = orderData || {};
	const order = safeOrderData.order || {};
	const details = safeOrderData.details || {};
	const products = Array.isArray(safeOrderData.products) ? safeOrderData.products : [];
	const { selectedSite, selectedSiteLegalEnitity } = useSite();
	const { stockImageUrl } = useBrandingAssets();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { addToCart } = useCart();
	const { user, canAddToCart, canAddToRequestList } = useAuth();
	const canShowOrderAgainCart = canAddToCart();
	const canShowOrderAgainRequestList = canAddToRequestList();
	const canShowOrderAgain = canShowOrderAgainCart || canShowOrderAgainRequestList;
	const [expanded, setExpanded] = useState({}); // key by product_id or index
	const unavailablePartMessage = t("UI_TEXT.PART_NOT_AVAILABLE_PURCHASE", {
		defaultValue: "This part is not available to purchase. Please contact CCR.",
	});

	
	const isPastSalesOrder =
		pastOrder || String(safeOrderData?.source || order?.source || "").toLowerCase() === "past";

	const findDeliveryTerm = (descOrTerm, legalEntity = "AUSA") => {
		const options = DT_BY_LE[legalEntity] || DT_BY_LE["AUSA"];
		const match =
			options.find(
				(o) => o.descriptionKey === descOrTerm || t(o.descriptionKey) === descOrTerm || o.term === descOrTerm
			) || {};
		return match.term ? `${match.term} - ${t(match.descriptionKey)}` : descOrTerm || "N/A";
	};

	const findDeliveryMode = (descOrCode, legalEntity = "AUSA") => {
		const options = DM_BY_LE[legalEntity] || DM_BY_LE["AUSA"];
		const match =
			options.find(
				(o) => o.description === descOrCode || o.code === descOrCode
			) || {};
		return match.code ? `${match.code} - ${match.description}` : descOrCode || "N/A";
	};

	const formatShipComplete = (value) => {
		const normalized = String(value || "").trim().toLowerCase();
		if (normalized === "true" || normalized === "yes") return "Yes";
		if (normalized === "false" || normalized === "no") return "No";
		return value || "N/A";
	};


	const handleNavigateToProduct = (url) => {
		navigate(url);
	};

	const handleImgError = (e) => {
		const defaultImage = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
		e.target.onerror = null; // Prevent infinite loop
		e.target.src = defaultImage; // Fallback to default image
	};

	const formatAddress = (parts) => {
		return parts
			.map((part) => part?.trim())
			.filter(Boolean)
			.join(", ");
	};

	const toggleExpand = (key) => {
		setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const buildTrackingUrl = (trackingNumber, trackingUrl) => {
		const cleanedUrl = trackingUrl ? String(trackingUrl).trim() : "";
		if (cleanedUrl) return cleanedUrl;
		return "";
	};

	const handleAddToCart = async (product) => {
		if (!canShowOrderAgainCart) return;
		const resolvedProductId = product?.productsData?.product_id || product?.product_id;
		const resolvedMachineId = product?.productsData?.machine_id || product?.machine_id;
		if (!resolvedProductId || !resolvedMachineId) {
			triggerToast(
				unavailablePartMessage,
				"warning"
			);
			return;
		}
		try {
			await addToCart({
				ProductID: resolvedProductId,
				MachineID: resolvedMachineId,
				Quantity: 1,
				Site: selectedSite,
			});
			triggerToast(
				t("ALERT_MESSAGES.PRODUCT_ADDED_TO_CART", {
					productNumber: product?.productsData?.product_number || product?.product_id,
				}),
				"success"
			);
		} catch (e) {
			triggerToast(t("ALERT_MESSAGES.ERROR_OCCURED") || "Error adding to cart", "error");
		}
	};

	const handleAddToRequestList = async (product, machineId) => {
		if (!canShowOrderAgainRequestList) return;
		const resolvedProductId = product?.productsData?.product_id || product?.product_id;
		const resolvedMachineId = machineId || product?.productsData?.machine_id || product?.machine_id;
		if (!resolvedProductId || !resolvedMachineId) {
			triggerToast(
				unavailablePartMessage,
				"warning"
			);
			return;
		}
		try {
			const req_body = {
				machine_id: resolvedMachineId,
				product_id: resolvedProductId,
				site_number: selectedSite,
				user_id: user?.user_id,
			};
			const response = await saveRequestList(req_body);
			if (response.status) {
				triggerToast(
					t("ALERT_MESSAGES.PRODUCT_ADDED_TO_REQUEST", {
						productName: product?.productsData?.product_name || product?.product_id,
					}),
					"success"
				);
			} else {
				const responseMessage = String(
					response?.message || response?.error_message || response?.error || ""
				).toLowerCase();
				if (responseMessage.includes("already")) {
					triggerToast(
						t("ALERT_MESSAGES.PRODUCT_ALREADY_IN_REQUEST", {
							productName: product?.productsData?.product_name || product?.product_id,
						}),
						"warning"
					);
				} else {
					triggerToast(
						unavailablePartMessage,
						"warning"
					);
				}
			}
		} catch (e) {
			triggerToast(t("ALERT_MESSAGES.ERROR_OCCURED") || "Error adding to request list", "error");
		}
	};
	const getLineStatusLabel = (item = {}) => {
		if (isPastSalesOrder) {
			const rawPastStatus =
				item?.sales_order_line_status ??
				item?.line_status ??
				item?.order_line_status ??
				item?.lineStatus;
			const normalized = rawPastStatus !== undefined && rawPastStatus !== null
				? String(rawPastStatus).trim()
				: "";
			if (normalized) {
				const parsed = Number.parseInt(normalized, 10);
				const mappedKey = Number.isFinite(parsed) ? String(parsed) : normalized;
				return PAST_SALES_LINE_STATUS_MAP[mappedKey] || normalized;
			}
		}

		return (
			item?.line_status ||
			item?.order_status ||
			order?.order_status ||
			"Pending"
		);
	};

	const showPartNumberColumn = partDetailsOnly;
	const partDetailsColSpan = (showPartNumberColumn ? 4 : 3) + (canShowOrderAgain ? 1 : 0);
	const renderPartDetailsSection = () => (
		<div className="right" >
			<div className="section" style={{ backgroundColor: '#fff' }}>
				{!partDetailsOnly && (
					<>
						<Typography variant="body1" className="section-title">
							{t('UI_TEXT.PART_DETAILS')}
						</Typography>
						<Divider className="divider" />
					</>
				)}
				<TableContainer className="product-details-container" style={{ padding: "10px 10px 10px 0" }}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>{t('TABLE_HEADERS.PART_NAME')}</TableCell>
								{showPartNumberColumn ? <TableCell align="left">{t('LABELS.PART_NUMBER') || "Part #"}</TableCell> : null}
								<TableCell align="center">Ordered Qty</TableCell>
								<TableCell align="center">Line Status</TableCell>
								{canShowOrderAgain ? <TableCell align="center" width={120}>Order Again</TableCell> : null}
							</TableRow>
						</TableHead>
						<TableBody>
							{products?.length > 0 ? (
								products.map((item, index) => {
									if (!item || !item.productsData) {
										return (
											<TableRow key={index}>
												<TableCell colSpan={partDetailsColSpan}>
													{t('UI_TEXT.ERROR_INVALID_PRODUCT')}
												</TableCell>
											</TableRow>
										);
									}
									const { productsData, product_quantity = 0 } = item;
									const productName = productsData?.product_name || null;
									const productNumber = productsData?.product_number || null;
									const lineStatusLabel = getLineStatusLabel(item);
									const machineId = productsData?.machine_id || '';
									const productId = productsData?.product_id || '';
									const isPartAvailableInSelectedSite = Boolean(productId && machineId);
									const trackingDetails = Array.isArray(item.tracking_details)
										? item.tracking_details
										: null;
									const hasTrackingDetails = Array.isArray(trackingDetails) && trackingDetails.length > 0;
									const hasTrackingData = trackingDetails !== null;
									const showTrackingSkeleton = trackingLoading && !hasTrackingData;
									const productImageSrc = (() => {
										const raw = productsData?.asset_data?.images?.[0];
										if (raw) {
											if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
											const path = raw.startsWith("/") ? raw : `/${raw}`;
											return `${ASSETS_BASE_URL}${path}`;
										}
										return `${ASSETS_BASE_URL}/ProductImages/TMB/${productsData?.product_number}.JPG`;
									})();
									const rowKey = productId || index;
									const isExpanded = !!expanded[rowKey];
									return (
										<React.Fragment key={rowKey}>
											<TableRow className={`product-row${isExpanded ? " product-row-expanded" : ""}`}>
												<TableCell className="product-cell">
													<Tooltip title={expanded[rowKey] ? t('BUTTON.COLLAPSE') || 'Collapse' : t('BUTTON.EXPAND') || 'Expand'}>
														<IconButton size="small" onClick={() => toggleExpand(rowKey)}>
															{expanded[rowKey] ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
														</IconButton>
													</Tooltip>
													<img
														src={productImageSrc}
														alt={productName}
														onError={handleImgError}
														className="product-image"
													/>
													<Box display={"flex"} flexDirection={"column"} justifyContent={"center"} width={"100%"} alignItems={"flex-start"}>
														{isPartAvailableInSelectedSite ? (
															<Link
																underline="hover"
																sx={{ cursor: "pointer" }}
																onClick={() => handleNavigateToProduct(`/machines/${machineId}/${productId}`)}>
																<span style={{ display: 'inline-block', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{productName}</span>
															</Link>
														) : (
															<Typography variant="body1" fontSize={"12px"} sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
																{productName || t('FALLBACK_TEXT.PRODUCT_NOT_AVAILABLE')}
															</Typography>
														)}
														<Typography variant="body1" fontSize={"11px"} color="text.secondary" >
															<span style={{ display: 'inline-block', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
																Part #: {productNumber || t('FALLBACK_TEXT.PRODUCT_NOT_AVAILABLE')}
															</span>
														</Typography>
														<ReplacementPartNotice product={productsData} linkToReplacement />
													</Box>
												</TableCell>
												{showPartNumberColumn ? (
													<TableCell align="left">
														{productNumber || t('FALLBACK_TEXT.PRODUCT_NOT_AVAILABLE')}
													</TableCell>
												) : null}
												<TableCell align="center">x {product_quantity}</TableCell>
												<TableCell align="center">{lineStatusLabel}</TableCell>
												{canShowOrderAgain ? (
													<TableCell align="center">
														<Box display="flex" justifyContent="center" alignItems="center" columnGap={1}>
															{isPartAvailableInSelectedSite && (
																productsData?.purchasable === 1 ? canShowOrderAgainCart : canShowOrderAgainRequestList
															) ? (
															<Tooltip title="Order Again">
																<IconButton color="primary" size="small"
																	onClick={() => {
																		if (productsData?.purchasable === 1) handleAddToCart(item); else handleAddToRequestList(item, machineId);
																	}}
																>
																	{productsData?.purchasable === 1 ? <AddShoppingCart fontSize="small" /> : <SummarizeIcon fontSize="small" />}
																</IconButton>
															</Tooltip>
														) : (
															<Typography variant="body2" color="text.secondary">—</Typography>
														)}
														</Box>
													</TableCell>
												) : null}
											</TableRow>
											{expanded[rowKey] && (
												<TableRow className="product-master-detail">
													<TableCell colSpan={partDetailsColSpan} sx={{ paddingTop: 0, paddingBottom: 2, paddingLeft: 2, paddingRight: 2 }}>
														<Box sx={{
															borderRadius: 1, border: "1px solid #e6e8eb",
															borderRadius: 2, backgroundColor: "#fdfdfd", px: 2.5, py: 1.5, display: "flex", flexDirection: "column", rowGap: 1
														}}>
															<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
																{t("UI_TEXT.TRACKING_DETAILS")}
															</Typography>
															{showTrackingSkeleton ? (
																<Box display="flex" flexDirection="column" rowGap={1}>
																	<Skeleton variant="rounded" height={36} />
																	<Skeleton variant="rounded" height={36} />
																</Box>
															) : hasTrackingDetails ? (
																<Box display="flex" flexDirection="column" rowGap={0.75}>
																	{trackingDetails.map((detail, trackingIndex) => {
																		const trackingNumber = detail?.tracking_number ? String(detail.tracking_number).trim() : "";
																		const trackingUrl = buildTrackingUrl(trackingNumber, detail?.tracking_url);
																		const label = trackingNumber
																			? `${t("UI_TEXT.TRACKING_NUMBER")} ${trackingNumber}`
																			: `${t("UI_TEXT.TRACKING_NUMBER")} ${trackingIndex + 1}`;
																		return (
																			<Box
																				key={`${rowKey}-tracking-${trackingIndex}`}
																				sx={{
																					display: "flex",
																					alignItems: "center",
																					justifyContent: "space-between",
																					gap: 1,
																					padding: "8px 12px",
																					borderRadius: 1,
																					backgroundColor: "#fff",
																					border: "1px solid #e6e8eb",
																				}}
																			>
																				<Typography variant="body2" sx={{ fontWeight: 600 }}>
																					{label}
																				</Typography>
																				{trackingUrl ? (
																					<Link
																						href={trackingUrl}
																						target="_blank"
																						rel="noopener"
																						sx={{ fontSize: 12 }}
																					>
																						{t("UI_TEXT.VIEW_TRACKING")}
																					</Link>
																				) : (
																					<Typography variant="caption" color="text.secondary">
																						{t("UI_TEXT.NO_TRACKING_DETAILS")}
																					</Typography>
																				)}
																			</Box>
																		);
																	})}
																</Box>
															) : (
																<Typography variant="body2" color="text.secondary">
																	{t("UI_TEXT.NO_TRACKING_DETAILS")}
																</Typography>
															)}
														</Box>
													</TableCell>
												</TableRow>
											)}
										</React.Fragment>
									);
								})
							) : (
								<TableRow key={"NoProductsRow"}>
									<TableCell colSpan={partDetailsColSpan}>
										<Typography variant="subtitle2" color="text.secondary" fontSize={"11px"} fontStyle={'italic'}>
											{t('UI_TEXT.ERROR_NO_PRODUCTS')}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</div>
		</div>
	);

	if (partDetailsOnly) {
		return renderPartDetailsSection();
	}
	return (
		<div className="order-details-section" sx={{ mt: 0 }}>
			<div className="left">
				<div className="section flex-space-between">
					<div className="flex-1">
						<Typography variant="body1" className="section-title">
							{t('UI_TEXT.SHIPPING_ADDRESS')}
						</Typography>
						<Divider className="divider" />
						<div className="shipping-address" style={{ padding: "10px 10px 10px 0" }}>
							<Typography variant="body1" className="mt-2">
								{details.shipping_name}
							</Typography>
							<Typography variant="body1">
								{formatAddress([
									details.shipping_street,
									details.shipping_city,
									details.shipping_state,
									details.shipping_zip
								])}
							</Typography>
							<Typography variant="body1">
								{details.shipping_country}
							</Typography>
						</div>
					</div>
					<div className="flex-1">
						<Typography variant="body1" className="section-title mt-2">
							{t('UI_TEXT.BILLING_ADDRESS')}
						</Typography>
						<Divider className="divider" />
						<div className="billing-address" style={{ padding: "10px 10px 10px 0" }}>
							<Typography variant="body1" className="mt-2">
								{details.billing_name}
							</Typography>
							<Typography variant="body1">
								{formatAddress([
									details.billing_street,
									details.billing_city,
									details.billing_state,
									details.billing_zip
								])}
							</Typography>
							<Typography variant="body1">
								{details.billing_country}
							</Typography>
						</div>
					</div>
				</div>
				<div className="section">
					<Typography variant="body1" className="section-title">
						{t('UI_TEXT.ADDITIONAL_ORDER_DETAILS')}
					</Typography>
					<Divider className="divider" />
					<div className="details flex-space-between">
						<div className="headings" style={{ padding: "10px 10px 10px 0" }}>
							<Typography variant="body1" className="mt-2 bold">
								{t('UI_TEXT.PO_NUMBER')}
							</Typography>
							<Typography variant="body1" className="bold">
								{t('UI_TEXT.DELIVERY_TERMS')}
							</Typography>
							<Typography variant="body1" className="bold">
								{t('LABELS.PREFERRED_CARRIER')}
							</Typography>
							<Typography variant="body1" className="bold">
								{t('LABELS.YOUR_CARRIER_NUMBER')}
							</Typography>
							<Typography variant="body1" className="bold">
								{t('UI_TEXT.SHIP_COMPLETE')}
							</Typography>
						</div>
						<div className="headings-values" style={{ padding: "10px 10px 10px 0" }}>
							<Typography
								variant="body1"
								sx={{
									fontSize: details.po_number === "" ? "0.875rem" : "1rem",
									fontStyle: details.po_number === "" ? "italic" : "normal",
									color: details.po_number === "" ? "#aaa" : "inherit",
								}}>
								{details.po_number || "N/A"}
							</Typography>


							<Typography
								variant="body1"
								sx={{
									fontSize: details.delivery_terms === "" ? "0.875rem" : "1rem",
									fontStyle: details.delivery_terms === "" ? "italic" : "normal",
									color: details.delivery_terms === "" ? "#aaa" : "inherit",
								}}>
								{findDeliveryTerm(details.delivery_terms, selectedSiteLegalEnitity)}
							</Typography>

							<Typography
								variant="body1"
								sx={{
									fontSize: details.mode_of_delivery === "" ? "0.875rem" : "1rem",
									fontStyle: details.mode_of_delivery === "" ? "italic" : "normal",
									color: details.mode_of_delivery === "" ? "#aaa" : "inherit",
								}}>
								{findDeliveryMode(details.mode_of_delivery, selectedSiteLegalEnitity)}
							</Typography>

							<Typography
								variant="body1"
								sx={{
									fontSize: details.freight_number === "" ? "0.875rem" : "1rem",
									fontStyle: details.freight_number === "" ? "italic" : "normal",
									color: details.freight_number === "" ? "#aaa" : "inherit",
								}}>
								{details.freight_number || "N/A"}
							</Typography>

							<Typography
								variant="body1"
								sx={{
									fontSize: details.ship_complete === "" ? "0.875rem" : "1rem",
									fontStyle: details.ship_complete === "" ? "italic" : "normal",
									color: details.ship_complete === "" ? "#aaa" : "inherit",
								}}>
								{formatShipComplete(details.ship_complete)}
							</Typography>
						</div>
					</div>

					{details.po_filename && order?.order_payment_mode !== "CC" && (
						<>
							<Typography variant="body1" className="section-title">
								Attached PO File
							</Typography>
							<Divider className="divider" />
							<Typography variant="body2" sx={{ mt: 0.5 }}>
								<Link
									href={`${ASSETS_BASE_URL}${details?.po_file_url}`}
									target="_blank"
									rel="noreferrer"
									underline="hover"
									sx={{ fontWeight: 500 }}
								>
									{details.po_filename}
								</Link>
							</Typography>
						</>
					)}
				</div>
				<div className="section full-width">
					<Typography variant="body1" className="section-title mt-2">
						{t('UI_TEXT.ORDER_NOTES')}
					</Typography>
					<Divider className="divider" />
					<Typography variant="body1" sx={{ padding: "10px 10px 10px 0" }}>
						{details.order_notes}
					</Typography>
				</div>
			</div>
			{renderPartDetailsSection()}
		</div>
	);
};

export default OrderDetails;
const triggerToast = (message, severity = "success") => {
	if (!message) return;
	toast.trigger({ message, severity });
};
